import mapboxgl from "mapbox-gl";
import {
    MapboxMap,
    commandRegistry,
    drawCommandRegistry,
    customDrawModes,
} from "./mapbox-map";
import { MapboxMapMarker } from "./mapbox-marker";
import { MapboxMapPopup } from "./mapbox-popup";
import { DrawCustomMode } from "@mapbox/mapbox-gl-draw";

interface EasingFunctions {
    [key: string]: (t: number) => number;
}

interface RegisterPortsSettings {
    outgoingPort?: string;
    outgoingDrawPort?: string;
    incomingPort?: string;
    easingFunctions?: EasingFunctions;
}

interface RegisterCustomElementSettings {
    token?: string;
}

interface MapEvent {
    target: string;
    command: string;
    bounds?: mapboxgl.LngLatBoundsLike;
    options?: any;
    offset?: [number, number];
    location?: mapboxgl.LngLatLike;
    zoom?: number;
    bearing?: number;
    url?: string;
    requestId?: string;
    query?: any;
}

interface DrawEvent {
    target: string;
    command: string;
    options?: any;
}

export function registerCustomElements(
    settings: RegisterCustomElementSettings,
): void {
    if (settings.token) {
        mapboxgl.accessToken = settings.token;
    }

    customElements.define("elm-mapbox-map", MapboxMap);
    customElements.define("elm-mapbox-marker", MapboxMapMarker);
    customElements.define("elm-mapbox-popup", MapboxMapPopup);
}

export function registerCustomDrawModes(modes: {
    [modeKey: string]: DrawCustomMode;
}) {
    for (const key in modes.keys) {
        customDrawModes[key] = modes[key];
    }
}

export function registerPorts(
    elmApp: any,
    settings: RegisterPortsSettings = {},
): any {
    const options: Required<RegisterPortsSettings> = Object.assign(
        {
            outgoingPort: "elmMapboxOutgoing",
            outgoingDrawPort: "elmMapboxDrawOutgoing",
            incomingPort: "elmMapboxIncoming",
            easingFunctions: {
                linear: (t: number) => t,
            },
        },
        settings,
    );

    if (elmApp.ports) {
        function processOptions(opts: any): any {
            if (opts.easing && typeof opts.easing === "string") {
                return Object.assign({}, opts, {
                    easing: options.easingFunctions[opts.easing],
                });
            }
            return opts;
        }

        function waitForMap(
            target: string,
            cb: (map: mapboxgl.Map) => void,
        ): void {
            const el = document.getElementById(target) as any;
            if (el && el.map) {
                cb(el.map);
            } else {
                let queue = commandRegistry[target];
                if (!queue) {
                    queue = commandRegistry[target] = [];
                }
                queue.push(cb);
            }
        }

        function waitForDraw(
            target: string,
            cb: (map: MapboxDraw) => void,
        ): void {
            const el = document.getElementById(target) as any;
            if (el && el.map && el._draw) {
                cb(el._draw);
            } else {
                let queue = drawCommandRegistry[target];
                if (!queue) {
                    queue = drawCommandRegistry[target] = [];
                }
                queue.push(cb);
            }
        }

        elmApp.ports[options.outgoingPort]?.subscribe((event: MapEvent) => {
            waitForMap(event.target, function (map: mapboxgl.Map) {
                switch (event.command) {
                    case "resize":
                        return map.resize();

                    case "fitBounds":
                        return map.fitBounds(
                            event.bounds!,
                            processOptions(event.options),
                        );

                    case "panBy":
                        return map.panBy(
                            event.offset!,
                            processOptions(event.options),
                        );

                    case "panTo":
                        return map.panTo(
                            event.location!,
                            processOptions(event.options),
                        );

                    case "zoomTo":
                        return map.zoomTo(
                            event.zoom!,
                            processOptions(event.options),
                        );

                    case "zoomIn":
                        return map.zoomIn(processOptions(event.options));

                    case "zoomOut":
                        return map.zoomOut(processOptions(event.options));

                    case "rotateTo":
                        return map.rotateTo(
                            event.bearing!,
                            processOptions(event.options),
                        );

                    case "jumpTo":
                        return map.jumpTo(processOptions(event.options));

                    case "easeTo":
                        return map.easeTo(processOptions(event.options));

                    case "flyTo":
                        return map.flyTo(processOptions(event.options));

                    case "stop":
                        return map.stop();

                    case "getBounds":
                        return elmApp.ports[options.incomingPort].send({
                            type: "getBounds",
                            id: event.requestId,
                            bounds: map.getBounds()?.toArray(),
                        });

                    case "queryRenderedFeatures":
                        return elmApp.ports[options.incomingPort].send({
                            type: "queryRenderedFeatures",
                            id: event.requestId,
                            features: event.query
                                ? map.queryRenderedFeatures(
                                      processOptions(event.options),
                                  )
                                : map.queryRenderedFeatures(
                                      event.query,
                                      processOptions(event.options),
                                  ),
                        });
                }
            });
        });

        elmApp.ports[options.outgoingDrawPort]?.subscribe(
            (event: DrawEvent) => {
                waitForDraw(event.target, function (draw: MapboxDraw) {
                    switch (event.command) {
                        case "add":
                            return draw.add(event.options);

                        case "set":
                            return draw.set(event.options);

                        case "changeMode":
                            return draw.changeMode(
                                event.options?.mode,
                                event.options?.options,
                            );

                        case "deleteAll":
                            return draw.deleteAll();
                    }
                });
            },
        );
    } else {
        // TODO: Update URL
        throw new Error(
            `Expected Elm App to expose ${options.outgoingPort} port. Please add https://github.com/gampleman/elm-mapbox/blob/master/examples/MapCommands.elm to your project and import it from your Main file.`,
        );
    }

    return elmApp;
}

export const supported: boolean = mapboxgl.supported();
